/**
 * Echeances des recurrences.
 *
 * Aucune occurrence n'est stockee : elles se deduisent de la regle sur la plage
 * demandee. Ce qui est stocke, ce sont les ecarts — une operation reelle qui
 * materialise une echeance (liee par `recurrenceId` + `occurrence`), ou une
 * exception `ignoree`.
 */

import {
  ajouterJours,
  ajouterMoisAncre,
  dateDeDayKey,
} from '../domain/temps'
import {
  PERIODICITE_MOIS,
  type Cents,
  type DayKey,
  type Exception,
  type Operation,
  type Recurrence,
} from '../domain/types'

/** Garde-fou contre une regle aberrante (intervalle nul, dates inversees). */
const MAX_OCCURRENCES = 5000

/**
 * Dates theoriques de la regle comprises dans `[du, au]`.
 *
 * Chaque date est recalculee **depuis l'ancrage** (`debut`), jamais depuis
 * l'echeance precedente : voir `ajouterMoisAncre` pour le 31 qui ne doit pas
 * deriver en 28.
 */
export function occurrencesTheoriques(r: Recurrence, du: DayKey, au: DayKey): DayKey[] {
  const fin = r.fin !== null && r.fin < au ? r.fin : au
  if (fin < r.debut || fin < du) return []

  const intervalle = Math.max(1, Math.floor(r.intervalle || 1))
  const out: DayKey[] = []

  if (r.periodicite === 'hebdomadaire') {
    const pas = 7 * intervalle
    // Saut direct au voisinage de `du` plutot que d'iterer depuis `debut` :
    // une recurrence hebdomadaire vieille de dix ans ne doit pas couter 520 tours.
    const ecart = Math.max(0, joursEntre(r.debut, du))
    let n = Math.floor(ecart / pas)
    for (let k = 0; k < MAX_OCCURRENCES; k++, n++) {
      const d = ajouterJours(r.debut, n * pas)
      if (d > fin) break
      if (d >= du) out.push(d)
    }
    return out
  }

  const pas = PERIODICITE_MOIS[r.periodicite] * intervalle
  const ecartMois = Math.max(0, moisEntreDates(r.debut, du))
  // On recule d'un pas : le rabattement en fin de mois peut faire tomber
  // l'echeance « n » avant `du` alors que la precedente n'y est pas encore.
  let n = Math.max(0, Math.floor(ecartMois / pas) - 1)
  for (let k = 0; k < MAX_OCCURRENCES; k++, n++) {
    const d = ajouterMoisAncre(r.debut, n * pas)
    if (d > fin) break
    if (d >= du) out.push(d)
  }
  return out
}

/** Jours calendaires entre deux dates, insensible aux changements d'heure. */
function joursEntre(a: DayKey, b: DayKey): number {
  const da = dateDeDayKey(a)
  const db = dateDeDayKey(b)
  const ua = Date.UTC(da.getFullYear(), da.getMonth(), da.getDate())
  const ub = Date.UTC(db.getFullYear(), db.getMonth(), db.getDate())
  return Math.round((ub - ua) / 86_400_000)
}

function moisEntreDates(a: DayKey, b: DayKey): number {
  const [ya = 0, ma = 0] = a.split('-').map(Number)
  const [yb = 0, mb = 0] = b.split('-').map(Number)
  return (yb - ya) * 12 + (mb - ma)
}

// ---------------------------------------------------------------------------
// Echeances enrichies
// ---------------------------------------------------------------------------

export type StatutEcheance = 'prevue' | 'realisee' | 'ignoree'

export interface Echeance {
  recurrence: Recurrence
  /** Date theorique, cle d'appariement avec l'operation reelle. */
  occurrence: DayKey
  statut: StatutEcheance
  operation: Operation | null
  /** Date a retenir : celle de l'operation si realisee, sinon la theorique. */
  date: DayKey
  /** Montant a retenir : le reel si realisee, sinon celui de la regle. */
  montant: Cents
  /** Prevue et deja depassee : probablement oubliee a la saisie. */
  enRetard: boolean
}

export function cleOccurrence(recurrenceId: string, occurrence: DayKey): string {
  return `${recurrenceId}|${occurrence}`
}

/**
 * Echeances de toutes les recurrences sur `[du, au]`, rapprochees des
 * operations reelles et des exceptions.
 *
 * Les recurrences inactives ne produisent plus d'echeance **prevue**, mais
 * leurs echeances deja realisees restent visibles : desactiver un abonnement
 * resilie ne doit pas faire disparaitre l'historique de ses prelevements.
 */
export function deriverEcheances(
  recurrences: readonly Recurrence[],
  exceptions: readonly Exception[],
  operations: readonly Operation[],
  du: DayKey,
  au: DayKey,
  aujourdhui: DayKey,
): Echeance[] {
  const realisees = new Map<string, Operation>()
  for (const op of operations) {
    if (op.recurrenceId && op.occurrence) {
      realisees.set(cleOccurrence(op.recurrenceId, op.occurrence), op)
    }
  }
  const ignorees = new Set<string>()
  for (const ex of exceptions) {
    if (ex.statut === 'ignoree') ignorees.add(cleOccurrence(ex.recurrenceId, ex.occurrence))
  }

  const out: Echeance[] = []
  for (const r of recurrences) {
    for (const occurrence of occurrencesTheoriques(r, du, au)) {
      const cle = cleOccurrence(r.id, occurrence)
      const operation = realisees.get(cle) ?? null
      if (operation) {
        out.push({
          recurrence: r,
          occurrence,
          statut: 'realisee',
          operation,
          date: operation.date,
          montant: operation.montant,
          enRetard: false,
        })
        continue
      }
      if (ignorees.has(cle)) {
        out.push({
          recurrence: r,
          occurrence,
          statut: 'ignoree',
          operation: null,
          date: occurrence,
          montant: 0,
          enRetard: false,
        })
        continue
      }
      if (!r.actif) continue
      out.push({
        recurrence: r,
        occurrence,
        statut: 'prevue',
        operation: null,
        date: occurrence,
        montant: r.montant,
        enRetard: occurrence < aujourdhui,
      })
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.recurrence.libelle.localeCompare(b.recurrence.libelle))
}

/** Prochaine echeance non encore realisee a partir de `depuis`, sur un an. */
export function prochaineEcheance(r: Recurrence, depuis: DayKey): DayKey | null {
  const horizon = ajouterJours(depuis, 400)
  return occurrencesTheoriques(r, depuis, horizon)[0] ?? null
}

/** Cout annualise d'une recurrence : utile pour comparer un abonnement
 *  mensuel a une prime annuelle sur une meme echelle. */
export function coutAnnuel(r: Recurrence): Cents {
  const intervalle = Math.max(1, r.intervalle || 1)
  if (r.periodicite === 'hebdomadaire') return Math.round((r.montant * 52) / intervalle)
  return Math.round((r.montant * 12) / (PERIODICITE_MOIS[r.periodicite] * intervalle))
}
